import { round2 } from "../../utils/money";
// frontend/src/pages/accounting/EditSalesTransaction.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  Package, Warehouse, Building, Calendar, Hash, DollarSign,
  ClipboardList, Percent, Trash2, User, Truck,
  CreditCard, FileText, Calculator, Save, X, Clock, AlertCircle,
  Loader2, RefreshCw, ArrowLeftRight, AlertTriangle,
  ArrowUp, ArrowDown, Download, Wallet,
  CreditCard as CreditCardIcon, Smartphone, Plus, Layers,
  Banknote, CircleDollarSign
} from "lucide-react";
import Select from "react-select";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./SalesTransaction.css";

const url = process.env.REACT_APP_BACKEND_URL;

const normalizeTransactionTypeName = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

const isSalesReturnType = (type) => {
  const name = normalizeTransactionTypeName(type?.transaction_type);
  const sign = String(type?.sign || "");
  return (
    name === "sales return" ||
    name === "sale return" ||
    name.includes("sales return") ||
    name.includes("sale return") ||
    (name.includes("return") && sign === "+")
  );
};

const isSalesType = (type) => {
  const name = normalizeTransactionTypeName(type?.transaction_type);
  const sign = String(type?.sign || "");
  return (
    !isSalesReturnType(type) &&
    ((name === "sale" || name === "sales") ||
      ((name.includes("sale") || name.includes("sales")) && sign === "-"))
  );
};

const getModeTransactionType = (types, returnMode) =>
  types.find((type) =>
    returnMode ? isSalesReturnType(type) : isSalesType(type),
  );

const EditSalesTransaction = () => {
  const { id } = useParams();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();
  const navigate = useNavigate();

  // State
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesTransaction, setSalesTransaction] = useState(null);
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [originalFinancialSnapshot, setOriginalFinancialSnapshot] = useState(null);
  // Dropdown data
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [paymentMethodsData, setPaymentMethodsData] = useState({
    methods: [],
  });
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [financialControlsReady, setFinancialControlsReady] = useState(false);

  // Batch dropdown state
  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);

  // Payment methods with account selection
  const [payments, setPayments] = useState([]);
  const [externalCustomerPaymentsTotal, setExternalCustomerPaymentsTotal] = useState(0);

  const getAccountsForMethod = (methodKey) =>
    paymentMethodsData.methods.find(
      (method) => method.key === methodKey,
    )?.accounts || [];

  // Form state
  const [formData, setFormData] = useState({
    warehouse_id: "",
    customer_id: "",
    transaction_type_id: "",
    invoice_no: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: new Date().toISOString().split('T')[0],
    notes: "",
    shipping_cost: "0",
    other_charges: "0",
    return_reason: "",
  });

  // Hidden status fields
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [transactionStatus, setTransactionStatus] = useState("pending");

  // Complete invoice product lines
  const [productsData, setProductsData] = useState([]);

  // Sales-level discount and tax controls, matching Add Sales.
  const [salesAdjustments, setSalesAdjustments] = useState({
    discount_id: "",
    tax_id: "",
    manual_discount: "0",
  });

  // Errors & focus
  const [focusedFields, setFocusedFields] = useState({});
  const [errors, setErrors] = useState({});

  // Totals
  const [totals, setTotals] = useState({
    subTotal: 0,
    totalDiscount: 0,
    totalTax: 0,
    shippingCost: 0,
    otherCharges: 0,
    grandTotal: 0,
    directAmountPaid: 0,
    totalAmountPaid: 0,
    netAmount: 0,
  });

  const isReturn = salesTransaction?.is_return || false;
  const isReturnModeRef = useRef(isReturn);

  const isFinanciallyPosted = Boolean(
    salesTransaction?.accounting_status === "posted" ||
    salesTransaction?.journal_transaction_id ||
    salesTransaction?.accounting_transaction_id ||
    salesTransaction?.inventory_posted ||
    salesTransaction?.stock_posted ||
    salesTransaction?.invoice?.accounting_status === "posted" ||
    salesTransaction?.invoice?.journal_transaction_id
  );

  // Helper: get company ID
  const getCompanyId = () => {
    if (salesTransaction) {
      return salesTransaction.company_id?._id || salesTransaction.company_id;
    }
    if (userType === 'company' && company) {
      return company._id || company;
    }
    return null;
  };

  // Load payment methods and receiving accounts using the same sales APIs
  const fetchSalesOptions = async (companyId) => {
    if (!companyId) return { methods: [], discounts: [], taxes: [] };

    try {
      setLoadingPaymentMethods(true);

      const [paymentTypeRes, accountRes] = await Promise.all([
        axios.get(`${url}/api/paymenttype/dropdown`, {
          params: { company_id: companyId, include_inactive: false },
        }),
        axios.get(`${url}/api/account/dropdown`, {
          params: { company_id: companyId, type: "Asset", include_inactive: false },
        }),
      ]);

      const paymentTypes = paymentTypeRes.data?.success
        ? paymentTypeRes.data.data || []
        : [];
      const accounts = accountRes.data?.success
        ? accountRes.data.data || []
        : [];

      const normalizeMethodKey = (value = "") => {
        const text = String(value).toLowerCase().trim().replace(/[\s-]+/g, "_");
        if (text.includes("mobile")) return "mobile_money";
        if (text.includes("bank")) return "bank";
        if (text.includes("credit")) return "credit";
        if (text.includes("cash")) return "cash";
        return text;
      };

      const configuredLabels = new Map(
        paymentTypes.map((type) => {
          const label = type.paymentstype || type.payment_type || type.name || type.label || "";
          return [normalizeMethodKey(label), label];
        }),
      );

      const definitions = [
        { key: "cash", label: configuredLabels.get("cash") || "Cash", name: configuredLabels.get("cash") || "Cash" },
        { key: "bank", label: configuredLabels.get("bank") || "Bank", name: configuredLabels.get("bank") || "Bank" },
        { key: "mobile_money", label: configuredLabels.get("mobile_money") || "Mobile Money", name: configuredLabels.get("mobile_money") || "Mobile Money" },
        { key: "credit", label: configuredLabels.get("credit") || "Credit", name: configuredLabels.get("credit") || "Credit" },
      ];

      const methods = definitions.map((definition) => ({
        ...definition,
        accounts: definition.key === "credit"
          ? []
          : accounts.filter((account) => {
            const accountName = String(
              account.account_name || ""
            ).toLowerCase();

            const isParentAccount =
              accountName === "cash" ||
              accountName === "bank account" ||
              accountName === "mobile money";

            if (isParentAccount) {
              return false;
            }

            if (definition.key === "cash") {
              return (
                accountName.includes("cash") ||
                accountName.includes("petty")
              );
            }

            if (definition.key === "bank") {
              return accountName.includes("bank");
            }

            if (definition.key === "mobile_money") {
              return (
                accountName.includes("mobile") ||
                accountName.includes("wallet") ||
                accountName.includes("evc") ||
                accountName.includes("edahab")
              );
            }

            return false;
          }),
      }));

      const [discountRes, taxRes] = await Promise.all([
        axios.get(`${url}/api/discounts`, {
          params: {
            company_id: companyId,
            status: "active",
          },
        }),
        axios.get(`${url}/api/taxes`, {
          params: {
            company_id: companyId,
            status: "active",
          },
        }),
      ]);

      const loadedDiscounts = discountRes.data?.success
        ? (discountRes.data.data || []).filter(
          (discount) =>
            discount.applies_to === "sale" ||
            discount.applies_to === "both"
        )
        : [];

      const loadedTaxes = taxRes.data?.success
        ? (taxRes.data.data || []).filter(
          (tax) =>
            tax.applies_to === "sale" ||
            tax.applies_to === "both"
        )
        : [];

      setPaymentMethodsData({ methods });
      setDiscounts(loadedDiscounts);
      setTaxes(loadedTaxes);

      return {
        methods,
        discounts: loadedDiscounts,
        taxes: loadedTaxes,
      };
    } catch (error) {
      setPaymentMethodsData({ methods: [] });
      setDiscounts([]);
      setTaxes([]);
      toast.error(
        error.response?.data?.message ||
        "Failed to load sales payment methods and accounts",
      );
      return { methods: [], discounts: [], taxes: [] };
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // ========== Warehouse-aware product list ==========
  const fetchProductsForWarehouse = async (warehouseId, batchNumber = null, sourceProducts = products) => {
    const normalizeProduct = (product = {}) => {
      const productId = product._id || product.product_id?._id || product.product_id;
      return {
        ...product,
        _id: productId,
        product_id: productId,
        product_code: product.product_code || product.code || product.sku || "N/A",
        product_name: product.product_name || product.name || "Unknown",
        selling_price: Number(
          product.sale_price ??
          product.selling_price ??
          product.sales_price ??
          product.unit_price ??
          0
        ),
      };
    };

    const normalizedSource = (Array.isArray(sourceProducts) ? sourceProducts : [])
      .map(normalizeProduct)
      .filter((product) => product._id);

    setFilteredProducts(normalizedSource);

    if (!warehouseId) {
      setProductStock({});
      return normalizedSource;
    }

    try {
      setLoadingProducts(true);
      const companyId = getCompanyId();
      const params = {
        company_id: companyId,
        status: "active",
      };
      if (batchNumber && batchNumber.trim() !== "") {
        params.batch_number = batchNumber;
      }

      const response = await axios.get(
        `${url}/api/inventorystock/warehouse/${warehouseId}`,
        { params },
      );

      if (!response.data?.success) {
        setProductStock({});
        return normalizedSource;
      }

      const stockMap = {};
      const productsById = new Map(
        normalizedSource.map((product) => [String(product._id), product]),
      );

      for (const stock of response.data.data || []) {
        const stockProduct =
          stock.product_id && typeof stock.product_id === "object"
            ? stock.product_id
            : {};
        const productId = stockProduct._id || stock.product_id;
        if (!productId) continue;

        const normalizedStockProduct = normalizeProduct({
          ...(productsById.get(String(productId)) || {}),
          ...stockProduct,
          _id: productId,
        });

        const salePrice = Number(
          stockProduct.sale_price ??
          stockProduct.selling_price ??
          stockProduct.sales_price ??
          stockProduct.unit_price ??
          normalizedStockProduct.selling_price ??
          0
        );

        stockMap[String(productId)] = {
          current_stock: Number(stock.quantity ?? 0),
          available_stock: Number(stock.available_qty ?? stock.quantity ?? 0),
          reserved_stock: Number(stock.reserved_qty ?? 0),
          avg_cost: Number(stock.avg_cost ?? 0),
          selling_price: salePrice,
          batch_number: stock.batch_number || "",
        };

        productsById.set(String(productId), {
          ...normalizedStockProduct,
          selling_price: salePrice || normalizedStockProduct.selling_price || 0,
        });
      }

      const mergedProducts = Array.from(productsById.values()).map((product) => {
        const stock = stockMap[String(product._id)] || {};
        return {
          ...product,
          current_stock: Number(stock.current_stock ?? 0),
          available_stock: Number(stock.available_stock ?? 0),
          selling_price: Number(
            stock.selling_price ?? product.selling_price ?? product.sale_price ?? 0
          ),
        };
      });

      const completeStockMap = { ...stockMap };
      for (const product of mergedProducts) {
        const key = String(product._id);
        if (!completeStockMap[key]) {
          completeStockMap[key] = {
            current_stock: 0,
            available_stock: 0,
            reserved_stock: 0,
            avg_cost: 0,
            selling_price: Number(product.selling_price || 0),
            batch_number: "",
          };
        }
      }

      setProductStock(completeStockMap);
      const filteredMerged = mergedProducts.filter(
        (product) => product.available_stock > 0
      );
      setFilteredProducts(filteredMerged);

      return mergedProducts;
    } catch (error) {
      setProductStock({});
      setFilteredProducts(normalizedSource);
      return normalizedSource;
    } finally {
      setLoadingProducts(false);
    }
  };

  // ========== Fetch distinct batch numbers for warehouse ==========
  const fetchBatchesForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setBatchOptions([]);
      setSelectedBatch(null);
      return;
    }

    try {
      const companyId = getCompanyId();
      const response = await axios.get(`${url}/api/inventorystock/batches`, {
        params: { warehouse_id: warehouseId, company_id: companyId }
      });
      if (response.data.success) {
        const batches = response.data.data || [];
        setBatchOptions(batches.map(b => ({ value: b, label: b })));
      }
    } catch (error) {
      setBatchOptions([]);
    }
  };

  // ========== Load stock when warehouse is preselected (edit mode) ==========
  useEffect(() => {
    if (warehouses.length > 0 && formData.warehouse_id) {
      fetchProductsForWarehouse(formData.warehouse_id, null, products);
      fetchBatchesForWarehouse(formData.warehouse_id);
    }
  }, [warehouses, formData.warehouse_id]);

  // Transaction type is controlled by the invoice mode.
  useEffect(() => {
    if (!salesTransaction || transactionTypes.length === 0) return;

    const returnMode = Boolean(
      salesTransaction.is_return || salesTransaction.invoice?.is_return
    );

    const expectedType = getModeTransactionType(
      transactionTypes,
      returnMode,
    );

    if (expectedType && String(formData.transaction_type_id) !== String(expectedType._id)) {
      setFormData((previous) => ({
        ...previous,
        transaction_type_id: expectedType._id,
      }));
    }
  }, [salesTransaction, transactionTypes, formData.transaction_type_id]);

  // Main data fetch
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const transactionRes = await axios.get(`${url}/api/sales/${id}`, {
          timeout: 10000
        });
        const transaction = transactionRes.data.data;
        const invoiceData = transaction.invoice || {};
        setSalesTransaction(transaction);
        isReturnModeRef.current = transaction.is_return || false;

        const companyId =
          transaction.company_id?._id ||
          transaction.company_id;

        const [loadedProducts, loadedSalesOptions] = await Promise.all([
          fetchAllDropdownData(companyId),
          fetchSalesOptions(companyId),
        ]);

        const paymentDetails =
          invoiceData.payment_details ||
          transaction.payment_details ||
          transaction.payments ||
          [];
        setOriginalFinancialSnapshot({
          warehouse_id: transaction.warehouse_id,
          customer_id: transaction.customer_id,
          invoice_no: invoiceData.invoice_no,
          transactions: transaction.invoice_transactions || [transaction],
          payments: paymentDetails
        });

        if (paymentDetails.length > 0) {
          setPayments(
            paymentDetails.map((payment) => ({
              method: payment.method || "credit",
              account_id:
                payment.account_id?._id ||
                payment.account_id ||
                "",
              account_name:
                payment.account_name ||
                payment.account_id?.account_name ||
                "",
              amount:
                payment.method === "credit"
                  ? ""
                  : String(payment.amount || ""),
              reference:
                payment.reference ||
                payment.reference_no ||
                "",
            })),
          );
        } else {
          const invoicePaymentMethod =
            invoiceData.payment_method ?? transaction.payment_method;
          const methods = Array.isArray(invoicePaymentMethod)
            ? invoicePaymentMethod
            : invoicePaymentMethod
              ? [invoicePaymentMethod]
              : ["credit"];

          setPayments(
            methods.map((method) => ({
              method,
              account_id: "",
              account_name: "",
              amount:
                method === "credit"
                  ? ""
                  : String(invoiceData.total_amount_paid ?? transaction.total_amount_paid ?? ""),
              reference: "",
            })),
          );
        }

        setFormData({
          warehouse_id: transaction.warehouse_id?._id || transaction.warehouse_id || "",
          customer_id: transaction.customer_id?._id || transaction.customer_id || "",
          transaction_type_id: transaction.transaction_type_id?._id || transaction.transaction_type_id || "",
          invoice_no: invoiceData.invoice_no || transaction.invoice_no || "",
          invoice_date: (invoiceData.invoice_date || transaction.transaction_date)
            ? new Date(invoiceData.invoice_date || transaction.transaction_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          due_date: (transaction.invoice?.due_date || transaction.due_date)
            ? new Date(transaction.invoice?.due_date || transaction.due_date).toISOString().split('T')[0]
            : new Date().toISOString().split('T')[0],
          notes: transaction.notes || "",
          shipping_cost: (transaction.invoice?.shipping_cost ?? transaction.shipping_cost)?.toString() || "0",
          other_charges: (transaction.invoice?.other_charges ?? transaction.other_charges)?.toString() || "0",
          return_reason: transaction.return_reason || "",
        });

        setPaymentStatus(invoiceData.payment_status || transaction.payment_status || "pending");
        setTransactionStatus(transaction.status || "pending");
        const externalPaymentsTotal = (transaction.customer_payments || []).reduce(
          (sum, payment) => sum + (Number(payment.total_paying_now) || 0),
          0,
        );
        setExternalCustomerPaymentsTotal(externalPaymentsTotal);

        const invoiceLines = Array.isArray(transaction.invoice_transactions) && transaction.invoice_transactions.length
          ? transaction.invoice_transactions
          : [transaction];

        setProductsData(
          invoiceLines.map((line) => ({
            _id: line._id,
            product_id:
              line.product_id?._id ||
              line.product_id ||
              "",
            quantity:
              String(Math.abs(Number(line.quantity || 0))),
            unit_price:
              round2(line.unit_price || 0),
            discount_id:
              line.discount_id?._id ||
              line.discount_id ||
              "",
            tax_id:
              line.tax_id?._id ||
              line.tax_id ||
              "",
            discount_percent:
              String(Number(line.discount_percent || 0)),
            discount_flat:
              String(Number(line.discount_flat || 0)),
            discount_amount:
              String(Number(line.discount_amount || 0)),
            tax_amount:
              String(Number(line.tax_amount || 0)),
            total_price:
              String(
                Number(
                  line.grand_total ??
                  line.total_price ??
                  0
                )
              ),
            batch_number:
              line.batch_number || "",
            expiry_date:
              line.expiry_date
                ? new Date(line.expiry_date)
                  .toISOString()
                  .split("T")[0]
                : "",
            notes:
              line.notes || "",
          }))
        );

        setSalesAdjustments({
          discount_id:
            invoiceData.discount_id?._id ||
            invoiceData.discount_id ||
            "",
          tax_id:
            invoiceData.tax_id?._id ||
            invoiceData.tax_id ||
            "",
          manual_discount:
            Number(
              invoiceData.manual_discount ||
              0
            ).toString(),
        });
        setFinancialControlsReady(true);
        if (transaction.warehouse_id?._id || transaction.warehouse_id) {
          const warehouseId = transaction.warehouse_id?._id || transaction.warehouse_id;
          await fetchProductsForWarehouse(warehouseId, null, loadedProducts);
          await fetchBatchesForWarehouse(warehouseId);
        }
      } catch (error) {
        setError(error.message);
        let errorMsg = "Failed to load sales transaction";
        if (error.response) {
          errorMsg = error.response.data?.message || errorMsg;
          if (error.response.status === 404) errorMsg = "Sales transaction not found";
        }
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && id) fetchData();
  }, [currentUser, id, navigate]);

  // Fetch all dropdown data (warehouses, customers, transaction types, products)
  const fetchAllDropdownData = async (companyId) => {
    if (!companyId) return;
    try {
      const params = { company_id: companyId, status: 'active' };

      const whRes = await axios.get(`${url}/api/warehouse/dropdown`, { params });
      if (whRes.data?.success) setWarehouses(whRes.data.data || []);

      const supRes = await axios.get(`${url}/api/customer/dropdown`, { params });
      if (supRes.data?.success) setCustomers(supRes.data.data || []);

      const ttRes = await axios.get(`${url}/api/transactiontype/dropdown`, { params });
      if (ttRes.data?.success) {
        const allTypes = ttRes.data.data || [];
        const salesTypes = allTypes.filter(
          (type) => isSalesType(type) || isSalesReturnType(type),
        );
        setTransactionTypes(salesTypes);
      }

      const prodRes = await axios.get(`${url}/api/products/dropdown`, {
        params,
      });

      const loadedProducts = prodRes.data?.success
        ? prodRes.data.data || []
        : [];

      setProducts(loadedProducts);
      setFilteredProducts(loadedProducts);

      return loadedProducts;
    } catch (error) {
      setProducts([]);
      setFilteredProducts([]);
      return [];
    }
  };

  // Handle warehouse change
  const handleWarehouseChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSelectedBatch(null);
    if (value) {
      fetchProductsForWarehouse(value, null, products);
      fetchBatchesForWarehouse(value);
    } else {
      setFilteredProducts(products);
      setProductStock({});
      setBatchOptions([]);
    }
  };

  const roundMoney = (value) =>
    Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

  const allocateDocumentAmount = (
    amount,
    lineIndex,
    sourceProducts = productsData,
  ) => {
    const grossValues = sourceProducts.map(
      (row) =>
        (Number(row.quantity) || 0) *
        (Number(row.unit_price) || 0),
    );
    const totalGross = grossValues.reduce(
      (sum, value) => sum + value,
      0,
    );
    const safeAmount = Math.max(0, Number(amount) || 0);

    if (
      safeAmount <= 0 ||
      totalGross <= 0 ||
      lineIndex < 0
    ) {
      return 0;
    }

    if (lineIndex === grossValues.length - 1) {
      const allocatedBefore = grossValues
        .slice(0, lineIndex)
        .reduce(
          (sum, gross) =>
            sum +
            roundMoney(
              (safeAmount * gross) / totalGross,
            ),
          0,
        );

      return Math.max(
        0,
        roundMoney(safeAmount - allocatedBefore),
      );
    }

    return roundMoney(
      (safeAmount * grossValues[lineIndex]) /
      totalGross,
    );
  };

  const calculateProductLine = (
    product,
    suppliedIndex = -1,
    sourceProducts = productsData,
  ) => {
    const quantity = Number(product.quantity) || 0;
    const unitCost = Number(product.unit_price) || 0;
    const gross = roundMoney(quantity * unitCost);
    const lineIndex =
      suppliedIndex >= 0
        ? suppliedIndex
        : sourceProducts.indexOf(product);

    const discount = discounts.find(
      (row) =>
        String(row._id) ===
        String(salesAdjustments.discount_id),
    );
    const tax = taxes.find(
      (row) =>
        String(row._id) ===
        String(salesAdjustments.tax_id),
    );

    let selectedDiscountAmount = 0;

    if (discount?.discount_type === "percentage") {
      selectedDiscountAmount = roundMoney(
        (gross * Number(discount.value || 0)) / 100,
      );
    } else if (discount?.discount_type === "fixed") {
      selectedDiscountAmount = allocateDocumentAmount(
        discount.value,
        lineIndex,
        sourceProducts,
      );
    }

    const manualDiscount = allocateDocumentAmount(
      salesAdjustments.manual_discount,
      lineIndex,
      sourceProducts,
    );

    const discountAmount = roundMoney(
      Math.min(
        gross,
        selectedDiscountAmount + manualDiscount,
      ),
    );
    const afterDiscount = roundMoney(
      Math.max(0, gross - discountAmount),
    );

    let selectedTaxAmount = 0;
    let exclusiveTax = 0;

    if (tax?.tax_type === "percentage") {
      const rate = Number(tax.value || 0);

      selectedTaxAmount = tax.price_inclusive
        ? roundMoney(
          afterDiscount -
          afterDiscount / (1 + rate / 100),
        )
        : roundMoney(
          (afterDiscount * rate) / 100,
        );

      exclusiveTax = tax.price_inclusive
        ? 0
        : selectedTaxAmount;
    } else if (tax?.tax_type === "fixed") {
      selectedTaxAmount = allocateDocumentAmount(
        tax.value,
        lineIndex,
        sourceProducts,
      );
      exclusiveTax = selectedTaxAmount;
    }

    const taxAmount = roundMoney(selectedTaxAmount);
    exclusiveTax = roundMoney(exclusiveTax);

    return {
      gross,
      discountAmount,
      taxAmount,
      exclusiveTax,
      total: roundMoney(
        afterDiscount + exclusiveTax,
      ),
      discountPercent:
        discount?.discount_type === "percentage"
          ? Number(discount.value || 0)
          : 0,
      discountFlat: 0,
    };
  };

  // Apply sales-level selections to every item.
  useEffect(() => {
    if (!financialControlsReady) return;

    const selectedDiscount = discounts.find(
      (row) =>
        String(row._id) ===
        String(salesAdjustments.discount_id),
    );
    const selectedTax = taxes.find(
      (row) =>
        String(row._id) ===
        String(salesAdjustments.tax_id),
    );

    setProductsData((current) => {
      let changed = false;

      const next = current.map((row, index) => {
        const calculated =
          calculateProductLine(
            row,
            index,
            current
          );

        const updated = {
          ...row,
          discount_id:
            salesAdjustments.discount_id || "",
          tax_id:
            salesAdjustments.tax_id || "",
          discount_percent:
            String(calculated.discountPercent || 0),
          discount_flat:
            String(calculated.discountFlat || 0),
          discount_amount:
            round2(calculated.discountAmount),
          tax_amount:
            round2(calculated.taxAmount),
          total_price:
            round2(calculated.total),
        };

        if (
          JSON.stringify(updated) !==
          JSON.stringify(row)
        ) {
          changed = true;
        }

        return updated;
      });

      return changed ? next : current;
    });
  }, [
    financialControlsReady,
    salesAdjustments,
    discounts,
    taxes,
    productsData
      .map(
        (row) =>
          `${row.quantity}:${row.unit_price}:${row.discount_amount}:${row.tax_amount}`,
      )
      .join("|"),
  ]);

  // Calculate totals using the same dynamic rules as Add Sales.
  useEffect(() => {
    let subTotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    let exclusiveTax = 0;

    productsData.forEach((product, index) => {
      const hasSavedFinancialValues =
        Number(product.discount_amount || 0) > 0 ||
        Number(product.tax_amount || 0) > 0;

      let calculated;

      const savedMode =
        hasSavedFinancialValues &&
        !salesAdjustments.discount_id &&
        !salesAdjustments.tax_id &&
        Number(salesAdjustments.manual_discount || 0) === 0;

      if (savedMode) {
        calculated = {
          gross:
            Number(product.quantity || 0) *
            Number(product.unit_price || 0),
          discountAmount:
            Number(product.discount_amount || 0),
          taxAmount:
            Number(product.tax_amount || 0),
          exclusiveTax:
            Number(product.tax_amount || 0),
          total:
            Number(product.total_price || 0),
          discountPercent:
            Number(product.discount_percent || 0),
          discountFlat:
            Number(product.discount_flat || 0),
        };
      } else {
        calculated =
          calculateProductLine(
            product,
            index,
            productsData
          );
      }

      subTotal += calculated.gross;
      totalDiscount += calculated.discountAmount;
      totalTax += calculated.taxAmount;
      exclusiveTax += calculated.exclusiveTax;
    });

    const shippingCost =
      Number(formData.shipping_cost) || 0;
    const otherCharges =
      Number(formData.other_charges) || 0;

    const grandTotal =
      subTotal -
      totalDiscount +
      exclusiveTax +
      shippingCost +
      otherCharges;

    const directAmountPaid = payments.reduce(
      (sum, payment) =>
        sum +
        (payment.method === "credit"
          ? 0
          : Number(payment.amount) || 0),
      0,
    );

    const totalAmountPaid = directAmountPaid + externalCustomerPaymentsTotal;

    const netAmount = Math.max(
      grandTotal - totalAmountPaid,
      0,
    );

    let nextPaymentStatus = "notpaid";

    if (totalAmountPaid > 0) {
      nextPaymentStatus =
        totalAmountPaid >= grandTotal - 0.005
          ? "paid"
          : "partial";
    }

    setPaymentStatus(nextPaymentStatus);

    setTotals({
      subTotal,
      totalDiscount,
      totalTax,
      shippingCost,
      otherCharges,
      grandTotal: isReturnModeRef.current
        ? -Math.abs(grandTotal)
        : grandTotal,
      directAmountPaid,
      totalAmountPaid,
      netAmount: isReturnModeRef.current
        ? -Math.abs(netAmount)
        : netAmount,
    });
  }, [
    productsData,
    formData.shipping_cost,
    formData.other_charges,
    payments,
    externalCustomerPaymentsTotal,
    discounts,
    taxes,
  ]);

  // Payment handlers
  const addPaymentRow = () => {
    setPayments((previous) => [
      ...(Array.isArray(previous) ? previous : []),
      {
        method: "cash",
        account_id: "",
        account_name: "",
        amount: "",
        reference: "",
      },
    ]);

    setErrors((previous) => ({
      ...previous,
      payments: "",
    }));
  };

  const removePaymentRow = (index) => {
    if (payments.length > 1) {
      setPayments((previous) =>
        previous.filter((_, rowIndex) => rowIndex !== index),
      );
    } else {
      toast.warning("At least one payment method is required");
    }
  };

  const handlePaymentChange = (index, field, value) => {
    const updated = payments.map((payment) => ({
      ...payment,
    }));

    if (field === "amount") {
      if (value === "" || /^\d*\.?\d*$/.test(value)) {
        updated[index].amount = value;
      }
    } else if (field === "method") {
      updated[index].method = value;
      updated[index].account_id = "";
      updated[index].account_name = "";
      if (value === "credit") {
        updated[index].amount = "";
      }
    } else if (field === "account_id") {
      updated[index].account_id = value;
      const selectedAccount = getAccountsForMethod(
        updated[index].method,
      ).find(
        (account) => String(account._id) === String(value),
      );
      updated[index].account_name =
        selectedAccount?.account_name || "";
    } else {
      updated[index][field] = value;
    }

    setPayments(updated);

    if (errors.payments) {
      setErrors((previous) => ({
        ...previous,
        payments: "",
      }));
    }
  };

  // Product handlers
  const addProductRow = () => {
    setProductsData(prev => [...prev, {
      _id: null,
      product_id: "",
      quantity: "",
      unit_price: "",
      discount_id: "",
      tax_id: "",
      discount_percent: "0",
      discount_flat: "0",
      discount_amount: "0",
      tax_amount: "0",
      total_price: "0",
      batch_number: selectedBatch || "",
      expiry_date: "",
      notes: "",
    }]);
  };

  const removeProductRow = (index) => {
    if (productsData.length > 1) setProductsData(prev => prev.filter((_, i) => i !== index));
    else toast.warning("At least one product is required");
  };

  const handleFocus = (field) => setFocusedFields(prev => ({ ...prev, [field]: true }));
  const handleBlur = (field) => {
    const value = formData[field];
    setFocusedFields(prev => ({ ...prev, [field]: (typeof value === 'string' ? value.trim() : value) ? true : false }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'warehouse_id') handleWarehouseChange(e);
    else if (['shipping_cost', 'other_charges'].includes(name)) {
      if (value === '' || /^\d*\.?\d*$/.test(value)) setFormData(prev => ({ ...prev, [name]: value }));
    } else setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  // Updated product selection: if a batch is selected, set the row's batch number to that batch
  const handleProductSelect = async (index, productId) => {
    try {
      if (productId) {
        const duplicate = productsData.some(
          (row, rowIndex) =>
            rowIndex !== index &&
            String(row.product_id) === String(productId)
        );
        if (duplicate) {
          toast.warning("This product is already added");
          return;
        }
      }

      setProductsData(prev => {
        const updated = [...prev];
        updated[index].product_id = productId;
        if (selectedBatch) {
          updated[index].batch_number = selectedBatch;
        }
        return updated;
      });

      if (productId) {
        const stockInfo = productStock[productId];
        let sellingPrice = 0;

        if (
          stockInfo &&
          Number(stockInfo.selling_price) > 0
        ) {
          sellingPrice = stockInfo.selling_price;
        } else {
          const companyId = getCompanyId();
          const response = await axios.get(
            `${url}/api/products/${productId}`,
            {
              params: {
                company_id: companyId
              }
            }
          );

          if (response.data.success) {
            const product = response.data.data;
            sellingPrice =
              product.sale_price ||
              product.selling_price ||
              product.sales_price ||
              product.unit_price ||
              0;
          }
        }

        setProductsData(previous => {
          const updated = previous.map(product => ({
            ...product
          }));
          updated[index].unit_price =
            round2(sellingPrice || 0);

          const calculated =
            calculateProductLine(
              updated[index],
              index,
              updated
            );

          updated[index].discount_percent =
            String(calculated.discountPercent);
          updated[index].discount_flat =
            String(calculated.discountFlat);
          updated[index].discount_amount =
            round2(calculated.discountAmount);
          updated[index].tax_amount =
            round2(calculated.taxAmount);
          updated[index].total_price =
            round2(calculated.total);

          return updated;
        });
      }
    } catch (error) {
    }
  };

  const handleProductChange = (index, field, value) => {
    setProductsData((previous) => {
      const updated = previous.map((product) => ({
        ...product,
      }));

      if (
        ["quantity", "unit_price"].includes(field) &&
        value !== "" &&
        !/^\d*\.?\d*$/.test(value)
      ) {
        return previous;
      }

      updated[index][field] = value;

      const calculated = calculateProductLine(updated[index], index, updated);

      updated[index].discount_percent =
        String(calculated.discountPercent);
      updated[index].discount_flat =
        String(calculated.discountFlat);
      updated[index].discount_amount =
        round2(calculated.discountAmount);
      updated[index].tax_amount =
        round2(calculated.taxAmount);
      updated[index].total_price =
        round2(calculated.total);

      return updated;
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.warehouse_id) newErrors.warehouse_id = "Warehouse is required";
    if (!formData.customer_id) newErrors.customer_id = "Customer is required";
    if (!formData.transaction_type_id) newErrors.transaction_type_id = "Transaction type is required";
    if (isReturnModeRef.current && !formData.return_reason) newErrors.return_reason = "Return reason is required";
    if (isFinanciallyPosted && adjustmentReason.trim().length < 10) {
      newErrors.adjustment_reason =
        "Adjustment reason must be at least 10 characters for a posted sale";
    }

    const validProducts = productsData.filter(p => p.product_id && p.quantity && p.unit_price && parseFloat(p.quantity) > 0 && parseFloat(p.unit_price) >= 0);
    if (validProducts.length === 0) newErrors.products = "At least one valid product is required";
    else {
      productsData.forEach((product, index) => {
        if (product.product_id && (!product.quantity || parseFloat(product.quantity) <= 0)) newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        if (product.product_id && (!product.unit_price || parseFloat(product.unit_price) < 0)) newErrors[`unit_price_${index}`] = "Unit price cannot be negative";
      });
    }

    const paidPayments = payments.filter(
      (payment) =>
        payment.method !== "credit" &&
        payment.amount &&
        Number(payment.amount) > 0,
    );

    const paymentWithoutAccount = paidPayments.find(
      (payment) => !payment.account_id,
    );
    if (paymentWithoutAccount) {
      const methodName = String(paymentWithoutAccount.method || "payment")
        .replaceAll("_", " ");
      newErrors.payments = `Please select an account for ${methodName}`;
    }

    const paidTotal = paidPayments.reduce(
      (sum, payment) => sum + Number(payment.amount || 0),
      0,
    );

    // ✅ UPDATED: better overpayment message with amounts
    if (paidTotal + externalCustomerPaymentsTotal > Math.abs(totals.grandTotal) + 0.005) {
      const entered = paidTotal + externalCustomerPaymentsTotal;
      const total = Math.abs(totals.grandTotal);
      const excess = Math.round((entered - total) * 100) / 100;
      const label = isReturnModeRef.current ? "Refund" : "Payment";
      newErrors.payments =
        `${label} exceeds the total by ${formatCurrency(excess)}. ` +
        `Total: ${formatCurrency(total)} • ` +
        `Entered: ${formatCurrency(entered)}` +
        (externalCustomerPaymentsTotal > 0
          ? ` (includes ${formatCurrency(externalCustomerPaymentsTotal)} previous customer payments)`
          : "") +
        `. Reduce the ${label.toLowerCase()} by ${formatCurrency(excess)} to continue.`;
    }

    setErrors(newErrors);

    const errorKeys = Object.keys(newErrors);

    if (errorKeys.length > 0) {
      const firstMessage = newErrors[errorKeys[0]];
      toast.error(
        firstMessage || "Please complete the required fields"
      );
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return false;
    }

    return true;
  };

  const handleCancel = () => navigate("/ViewSalesTransaction");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const validPayments = payments.filter(
        (payment) =>
          payment.method !== "credit" &&
          Number(payment.amount || 0) > 0 &&
          payment.account_id,
      );
      const validProducts = productsData.filter(p => p.product_id && p.quantity && p.unit_price && parseFloat(p.quantity) > 0 && parseFloat(p.unit_price) >= 0);
      const payload = {
        financial_edit_mode: Boolean(isFinanciallyPosted),
        adjustment_reason: adjustmentReason,
        original_financial_snapshot: originalFinancialSnapshot,
        update_inventory: true,
        inventory_update_strategy: "delta",
        warehouse_id: formData.warehouse_id,
        customer_id: formData.customer_id,
        transaction_type_id: formData.transaction_type_id,
        invoice_no: formData.invoice_no,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date,
        notes: formData.notes,
        shipping_cost: parseFloat(formData.shipping_cost) || 0,
        tax_amount: totals.totalTax,
        other_charges: parseFloat(formData.other_charges) || 0,
        manual_discount:
          Number(salesAdjustments.manual_discount || 0),
        discount_id:
          salesAdjustments.discount_id || null,
        tax_id:
          salesAdjustments.tax_id || null,
        status: transactionStatus,
        payment_status: paymentStatus,
        payment_method: validPayments.map(
          (payment) => payment.method
        ),
        total_amount_paid: totals.directAmountPaid,
        net_amount: Math.abs(totals.netAmount),
        payment_details: validPayments.map((payment) => ({
          method: payment.method,
          account_id: payment.account_id,
          amount: Number(payment.amount),
          reference: payment.reference || "",
        })),
        transactions: validProducts.map((product, index) => {
          let calculated;
          const savedMode =
            !salesAdjustments.discount_id &&
            !salesAdjustments.tax_id &&
            Number(salesAdjustments.manual_discount || 0) === 0;

          if (savedMode) {
            calculated = {
              gross:
                Number(product.quantity || 0) *
                Number(product.unit_price || 0),
              discountAmount:
                Number(product.discount_amount || 0),
              taxAmount:
                Number(product.tax_amount || 0),
              exclusiveTax:
                Number(product.tax_amount || 0),
              total:
                Number(product.total_price || 0),
              discountPercent:
                Number(product.discount_percent || 0),
              discountFlat:
                Number(product.discount_flat || 0)
            };
          } else {
            calculated =
              calculateProductLine(
                product,
                index,
                productsData
              );
          }

          return {
            _id: product._id || null,
            product_id: product.product_id,
            warehouse_id: formData.warehouse_id,
            customer_id: formData.customer_id,
            quantity: Number(product.quantity),
            unit_price: Number(product.unit_price),
            discount_id:
              salesAdjustments.discount_id || null,
            tax_id:
              salesAdjustments.tax_id || null,
            discount_percent:
              calculated.discountPercent,
            discount_flat: calculated.discountFlat,
            discount_amount:
              calculated.discountAmount,
            tax_amount: calculated.taxAmount,
            batch_number: product.batch_number || "",
            expiry_date: product.expiry_date || null,
            notes: product.notes || "",
          };
        }),
        return_reason: formData.return_reason,
        payments: validPayments.map((payment) => ({
          method: payment.method,
          account_id: payment.account_id,
          account_name: payment.account_name || "",
          amount: Number(payment.amount),
          reference: payment.reference || "",
        }))
      };

      const res = await axios.put(`${url}/api/sales/${id}`, payload, {
        headers: { "Content-Type": "application/json" }
      });

      if (res.data.success) {
        toast.success(
          res.data?.message ||
          "Sales invoice, stock and payment changes saved successfully"
        );
        setTimeout(() => navigate("/ViewSalesTransaction"), 1500);
      }
    } catch (error) {
      let errorMsg = "Failed to update sales transaction";
      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        if (error.response.data?.errors) {
          const serverErrors = {};
          if (Array.isArray(error.response.data.errors)) {
            error.response.data.errors.forEach(err => { if (err.field) serverErrors[err.field] = err.message; });
          } else if (typeof error.response.data.errors === 'object') {
            Object.keys(error.response.data.errors).forEach(key => { serverErrors[key] = error.response.data.errors[key]; });
          }
          setErrors(serverErrors);
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch { return "Invalid date"; }
  };

  const getPaymentMethodLabel = (method) => {
    const labels = { cash: 'Cash', bank: 'Bank', mobile_money: 'Mobile Money', credit: 'Credit' };
    return labels[method] || method;
  };

  const getPaymentStatusDisplayInfo = () => {
    if (paymentStatus === 'paid') return { text: 'Paid', color: '#52c41a', bg: '#f6ffed' };
    if (paymentStatus === 'partial') return { text: 'Partial', color: '#fa8c16', bg: '#fff7e6' };
    if (paymentStatus === 'notpaid') return { text: 'Not Paid', color: '#ff4d4f', bg: '#fff2f0' };
    return { text: paymentStatus || 'Pending', color: '#856404', bg: '#fff3cd' };
  };

  // ✅ Overpayment / Over-refund detection — works in BOTH Sales and Return mode
  const overpaidAmount = Math.max(
    0,
    totals.totalAmountPaid - Math.abs(totals.grandTotal)
  );
  const hasOverpayment = overpaidAmount > 0.005;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="sales-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading sales transaction data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !salesTransaction) {
    return (
      <div className="form-wrapper">
        <div className="sales-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Transaction Not Found</h2>
            <p>{error || "Sales transaction not found"}</p>
            <button className="cancel-btn" onClick={handleCancel} style={{ marginTop: '20px' }}>Back to List</button>
          </div>
        </div>
      </div>
    );
  }

  const paymentStatusDisplay = getPaymentStatusDisplayInfo();

  return (
    <div className="form-wrapper">
      <div className="sales-form-container">
        <button className="close-btn" onClick={handleCancel} type="button">
          <X size={20} />
        </button>

        <div className="form-header">
          <ClipboardList size={24} />
          <h2>Edit {isReturn ? 'Sales Return' : 'Sales Transaction'}</h2>
        </div>

        {isReturn && (
          <div
            style={{
              marginBottom: 20,
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              padding: '10px',
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: 4,
              color: '#ff4d4f'
            }}
          >
            <AlertTriangle size={20} />
            <span style={{ fontWeight: 600 }}>RETURN TRANSACTION - Editing a sales return</span>
            <ArrowUp size={16} />
          </div>
        )}

        {/* Payment Status Indicator */}
        <div style={{
          marginBottom: 20,
          padding: '10px 15px',
          background: paymentStatus === 'paid' ? '#f6ffed' :
            paymentStatus === 'partial' ? '#fff7e6' :
              paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
          border: `1px solid ${paymentStatus === 'paid' ? '#b7eb8f' : paymentStatus === 'partial' ? '#ffd591' : paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'}`,
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DollarSign size={16} color={paymentStatus === 'paid' ? '#52c41a' : paymentStatus === 'partial' ? '#fa8c16' : paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'} />
            <span style={{ fontWeight: 500 }}>Payment Status:</span>
          </div>
          <span style={{
            padding: '2px 8px',
            borderRadius: 12,
            fontSize: 11,
            fontWeight: 600,
            backgroundColor: paymentStatusDisplay.bg,
            color: paymentStatusDisplay.color
          }}>
            {paymentStatusDisplay.text}
          </span>
        </div>

        {/* Posted Sales Adjustment section */}
        {isFinanciallyPosted && (
          <div
            style={{
              padding: "16px 20px",
              borderRadius: "8px",
              marginBottom: "20px",
              background: "#fffbe6",
              border: "1px solid #ffd666",
              color: "#874d00",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                marginBottom: "10px",
                fontWeight: 700,
                fontSize: "16px",
              }}
            >
              <AlertTriangle size={20} />
              Full Posted Sales Adjustment
            </div>

            <p
              style={{
                margin: "0 0 12px 0",
                lineHeight: "1.6",
              }}
            >
              The complete form is editable. You may correct the warehouse,
              customer, products, quantities, selling prices, discounts, tax,
              charges, dates, and payment accounts. Saving sends the complete
              corrected sale to the backend while keeping the original audit trail.
            </p>

            <p
              style={{
                margin: "0 0 12px 0",
                color: "#cf1322",
                fontWeight: 600,
              }}
            >
              Your role does not normally include posted-sales editing.
              The backend will still enforce permission rules when you save.
            </p>

            <label
              style={{
                display: "block",
                marginBottom: "6px",
                fontWeight: 700,
                color: "#874d00",
              }}
            >
              Adjustment Reason *
            </label>

            <textarea
              value={adjustmentReason}
              onChange={(e) => setAdjustmentReason(e.target.value)}
              placeholder="Explain the original mistake and the correction, for example: Wrong warehouse and quantity selected during sales entry."
              rows="3"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "4px",
                border: "1px solid #d9d9d9",
                resize: "vertical",
                fontSize: "14px",
              }}
            />

            <small
              style={{
                display: "block",
                marginTop: "6px",
                color: "#8c8c8c",
              }}
            >
              At least 10 characters are required for the accounting audit trail.
            </small>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Info - same grid as Add Sales */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Warehouse *</label>
              <Select
                value={
                  warehouses
                    .filter(
                      (wh) =>
                        String(wh._id) === String(formData.warehouse_id)
                    )
                    .map((wh) => ({
                      value: wh._id,
                      label: `${wh.warehouse_name || wh.name || "Warehouse"} (${wh.warehouse_code || wh.code || "N/A"})`,
                    }))[0] || null
                }
                options={
                  warehouses.map((wh) => ({
                    value: wh._id,
                    label: `${wh.warehouse_name || wh.name || "Warehouse"} (${wh.warehouse_code || wh.code || "N/A"})`,
                  }))
                }
                onChange={(selected) => {
                  const value = selected ? selected.value : "";
                  setFormData(prev => ({
                    ...prev,
                    warehouse_id: value
                  }));
                  setSelectedBatch(null);
                  if (value) {
                    fetchProductsForWarehouse(value, null, products);
                    fetchBatchesForWarehouse(value);
                  } else {
                    setFilteredProducts(products);
                    setProductStock({});
                    setBatchOptions([]);
                  }
                }}
                placeholder={
                  warehouses.length === 0
                    ? "No warehouses available"
                    : "Search Warehouse..."
                }
                isClearable
                isDisabled={warehouses.length === 0}
                noOptionsMessage={() => "No warehouse found"}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Customer *</label>
              <Select
                value={
                  customers
                    .filter(
                      (customer) =>
                        String(customer._id) === String(formData.customer_id)
                    )
                    .map((customer) => ({
                      value: customer._id,
                      label:
                        `${customer.customer_name || customer.name || "Customer"} (${customer.customer_code || customer.code || customer.reference_id || "N/A"})`
                    }))[0] || null
                }
                options={
                  customers.map((customer) => ({
                    value: customer._id,
                    label:
                      `${customer.customer_name || customer.name || "Customer"} (${customer.customer_code || customer.code || customer.reference_id || "N/A"})`
                  }))
                }
                onChange={(selected) => {
                  setFormData(prev => ({
                    ...prev,
                    customer_id: selected
                      ? selected.value
                      : ""
                  }));
                }}
                placeholder={
                  customers.length === 0
                    ? "No customers available"
                    : "Search Customer..."
                }
                isClearable
                isDisabled={customers.length === 0}
                noOptionsMessage={() => "No customer found"}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    border: "1px solid #ddd",
                    borderRadius: "4px"
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Transaction Type</label>
              <select
                name="transaction_type_id"
                value={formData.transaction_type_id}
                disabled
                aria-readonly="true"
                style={{
                  width: '100%',
                  padding: 8,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  background: '#f5f5f5',
                  cursor: 'not-allowed',
                }}
                required
              >
                <option value="">
                  {transactionTypes.length === 0
                    ? 'Transaction type unavailable'
                    : 'Selecting transaction type...'}
                </option>
                {transactionTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.transaction_type} ({type.sign})
                  </option>
                ))}
              </select>
              <small style={{ color: '#666' }}>
                Automatically set by the transaction mode.
              </small>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice No (Optional)</label>
              <input
                type="text"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                placeholder="Auto-generated if empty"
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Invoice Date</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Due Date</label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
              />
            </div>

            {/* Batch Number Dropdown */}
            <div>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Batch Number</label>
              <Select
                value={selectedBatch ? { value: selectedBatch, label: selectedBatch } : null}
                options={batchOptions}
                onChange={async (selected) => {
                  const batch = selected ? selected.value : null;
                  setSelectedBatch(batch);
                  if (formData.warehouse_id) {
                    await fetchProductsForWarehouse(formData.warehouse_id, batch, products);
                    if (!batch) {
                      await fetchProductsForWarehouse(formData.warehouse_id, null, products);
                    }
                  }
                }}
                placeholder="Search Batch..."
                isClearable
                noOptionsMessage={() => "No batches found"}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: "4px",
                    border: "1px solid #ddd",
                  }),
                  menu: (base) => ({ ...base, zIndex: 9999 }),
                  menuList: (base) => ({ ...base, maxHeight: 250 }),
                }}
              />
              <small style={{ color: '#666' }}>Select a batch to filter products</small>
            </div>

            {isReturn && (
              <div style={{ gridColumn: 'span 2' }}>
                <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>Return Reason *</label>
                <input
                  type="text"
                  name="return_reason"
                  value={formData.return_reason}
                  onChange={handleChange}
                  placeholder="Reason for return..."
                  style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  required={isReturn}
                />
              </div>
            )}
          </div>

          {/* Products Section */}
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 15 }}>
            <Package size={18} /> Products
            {formData.warehouse_id && (
              <span style={{ fontSize: 13, color: '#666', fontWeight: 'normal' }}>
                (Showing stock for selected warehouse)
                {selectedBatch && <span style={{ color: '#1890ff' }}> - Batch: {selectedBatch}</span>}
              </span>
            )}
          </h3>

          {errors.products && (
            <div style={{ marginBottom: '15px', padding: '10px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px', color: '#ff4d4f', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertCircle size={16} />{errors.products}
            </div>
          )}

          {productsData.map((product, index) => (
            <div key={product._id || `new-${index}`} style={{ border: '1px solid #ddd', padding: 15, marginBottom: 15, borderRadius: 4, position: 'relative', background: '#fafafa' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '4fr 1fr 1.4fr 1.2fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Product</label>
                  <Select
                    value={
                      filteredProducts
                        .filter(
                          (prod) =>
                            String(prod._id) === String(product.product_id)
                        )
                        .map((prod) => ({
                          value: prod._id,
                          label:
                            `${prod.product_name} (${prod.product_code})` +
                            ` | Stock: ${productStock[prod._id]?.available_stock || 0}` +
                            ` | Avg Cost: ${formatCurrency(productStock[prod._id]?.avg_cost || 0)}`
                        }))[0] || null
                    }
                    options={
                      filteredProducts
                        .filter(prod => {
                          const alreadySelected =
                            productsData.some(
                              (row, rowIndex) =>
                                rowIndex !== index &&
                                String(row.product_id) === String(prod._id)
                            );
                          return !alreadySelected;
                        })
                        .map(prod => ({
                          value: prod._id,
                          label:
                            `${prod.product_name} (${prod.product_code})` +
                            ` | Stock: ${productStock[prod._id]?.available_stock || 0}` +
                            ` | Avg Cost: ${formatCurrency(productStock[prod._id]?.avg_cost || 0)}`
                        }))
                    }
                    onChange={(selected) => {
                      handleProductSelect(
                        index,
                        selected ? selected.value : ""
                      );
                    }}
                    placeholder="Search product..."
                    isClearable
                    isDisabled={!formData.warehouse_id}
                    isLoading={loadingProducts}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    noOptionsMessage={() =>
                      formData.warehouse_id
                        ? "No product found"
                        : "Select a warehouse first"
                    }
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        border: "1px solid #ddd"
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 99999
                      })
                    }}
                  />
                  {product.product_id && productStock[product.product_id] && (
                    <div style={{ fontSize: 11, marginTop: 4, color: '#52c41a' }}>
                      <Layers size={12} style={{ display: 'inline', marginRight: 4 }} />
                      Available: {productStock[product.product_id].available_stock || 0} units
                      {!isReturn && Number(productStock[product.product_id].avg_cost || 0) > 0 && (
                        <> | Avg Cost: {formatCurrency(productStock[product.product_id].avg_cost || 0)}</>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Quantity</label>
                  <input
                    type="number"
                    placeholder="Qty"
                    value={product.quantity}
                    onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                    required
                    min="1"
                    step="any"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Unit Price</label>
                  <input
                    type="number"
                    placeholder="Sale price"
                    value={product.unit_price}
                    onChange={(e) => handleProductChange(index, 'unit_price', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Line Total</label>
                  <input
                    type="text"
                    value={formatCurrency(Number(product.total_price) || 0)}
                    readOnly
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd', background: '#f5f5f5', fontWeight: 600, color: isReturn ? '#ff4d4f' : '#1890ff' }}
                  />
                </div>
              </div>

              {/* Batch and Expiry */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <div>
                  <input
                    type="text"
                    placeholder="Batch Number (optional)"
                    value={product.batch_number}
                    onChange={(e) => handleProductChange(index, 'batch_number', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#666' }}>Expire Date</label>
                  <input
                    type="date"
                    placeholder="Expiry Date"
                    value={product.expiry_date}
                    onChange={(e) => handleProductChange(index, 'expiry_date', e.target.value)}
                    style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </div>
              </div>

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeProductRow(index)}
                  style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    background: '#fff1f0',
                    border: '1px solid #ffccc7',
                    borderRadius: 4,
                    color: '#ff4d4f',
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addProductRow}
            style={{
              marginBottom: 20,
              padding: '8px 16px',
              background: 'transparent',
              border: '2px dashed #1890ff',
              borderRadius: 4,
              color: '#1890ff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
            disabled={!formData.warehouse_id}
          >
            <Plus size={16} /> Add Product
          </button>

          {!formData.warehouse_id && (
            <div style={{ fontSize: 13, color: '#fa8c16', marginBottom: 20 }}>
              Please select a warehouse first to see available products
            </div>
          )}

          {/* Discount and tax */}
          <h3 style={{ marginBottom: 15 }}>
            {isReturn ? 'Sales Return Discount and Tax' : 'Sales Discount and Tax'}
          </h3>
          <div style={{ padding: 18, border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 20, background: '#fafafa' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 6 }}>Apply Discount to All Items</label>
                <select
                  value={salesAdjustments.discount_id}
                  onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, discount_id: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}
                >
                  <option value="">No Discount</option>
                  {discounts.map((discount) => (
                    <option key={discount._id} value={discount._id}>
                      {discount.name} ({discount.code}) - {discount.discount_type === 'percentage' ? `${discount.value}%` : `${formatCurrency(discount.value)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6 }}>Apply Tax to All Items</label>
                <select
                  value={salesAdjustments.tax_id}
                  onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, tax_id: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}
                >
                  <option value="">No Tax</option>
                  {taxes.map((tax) => (
                    <option key={tax._id} value={tax._id}>
                      {tax.name} ({tax.code}) - {tax.tax_type === 'percentage' ? `${tax.value}%` : `${formatCurrency(tax.value)}`}
                      {tax.price_inclusive ? ' · Inclusive' : ' · Exclusive'}{tax.recoverable ? ' · Recoverable' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: 6 }}>Manual Discount Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Example: 5.00"
                  value={salesAdjustments.manual_discount}
                  onChange={(e) => setSalesAdjustments((prev) => ({ ...prev, manual_discount: e.target.value }))}
                  style={{ width: '100%', padding: 10, borderRadius: 4, border: '1px solid #ddd' }}
                />
                <small style={{ color: '#666' }}>This amount is added to the selected discount and allocated across items.</small>
              </div>
            </div>
          </div>

          {/* Additional Charges */}
          <h3 style={{ marginBottom: 15 }}>Additional Charges</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Shipping Cost</label>
              <input
                type="number"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                min="0"
                step="0.01"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 5 }}>Other Charges</label>
              <input
                type="number"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                min="0"
                step="0.01"
              />
            </div>
          </div>

          {/* Payments Section */}
          <h3 style={{ marginBottom: 15 }}>{isReturn ? 'Refund / Settlement' : 'Payments'}</h3>

          {errors.payments && (
            <div style={{ marginBottom: 15, padding: 10, background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4, color: '#ff4d4f', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertCircle size={16} />
              {errors.payments}
            </div>
          )}

          {loadingPaymentMethods ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <Loader2 className="animate-spin" size={24} />
              <p>Loading payment accounts...</p>
            </div>
          ) : paymentMethodsData.methods.length === 0 ? (
            <div style={{ padding: 20, background: '#fafafa', borderRadius: 8, marginBottom: 20, textAlign: 'center', border: '1px dashed #ddd' }}>
              <AlertCircle size={32} color="#fa8c16" />
              <p style={{ marginTop: 10, color: '#666' }}>
                No approved Cash, Bank, Mobile Money, or Other posting accounts are configured.
              </p>
            </div>
          ) : (
            <>
              {payments.map((payment, idx) => (
                <div key={idx} style={{ marginBottom: 15, padding: 15, background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0', position: 'relative' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr auto', gap: 10, alignItems: 'center' }}>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Payment Method</label>
                      <select
                        value={payment.method || ""}
                        onChange={(e) => handlePaymentChange(idx, 'method', e.target.value)}
                        style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                      >
                        <option value="">Select Payment Method</option>
                        {paymentMethodsData.methods.map((method) => (
                          <option key={method.key} value={method.key}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Account</label>
                      <select
                        value={payment.account_id || ""}
                        onChange={(e) => handlePaymentChange(idx, 'account_id', e.target.value)}
                        disabled={payment.method === 'credit'}
                        required={payment.method !== 'credit' && Number(payment.amount) > 0}
                        style={{
                          width: '100%',
                          padding: 8,
                          borderRadius: 4,
                          border: '1px solid #ddd',
                          background: payment.method === 'credit' ? '#f5f5f5' : 'white'
                        }}
                      >
                        <option value="">
                          {payment.method === 'credit' ? 'Accounts Receivable (automatic)' : 'Select Account'}
                        </option>
                        {getAccountsForMethod(payment.method).map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.account_code} - {account.account_name} (Balance: {formatCurrency(account.balance || 0)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: '#666', marginBottom: 4, display: 'block' }}>Amount</label>
                      <input
                        type="number"
                        placeholder={payment.method === 'credit' ? 'Remaining balance' : '0.00'}
                        value={payment.amount}
                        onChange={(e) => handlePaymentChange(idx, 'amount', e.target.value)}
                        disabled={payment.method === 'credit'}
                        min="0"
                        step="0.01"
                        style={{
                          width: '100%',
                          padding: 8,
                          borderRadius: 4,
                          border: '1px solid #ddd',
                          background: payment.method === 'credit' ? '#f5f5f5' : 'white'
                        }}
                      />
                    </div>
                    {payments.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePaymentRow(idx)}
                        style={{ marginTop: 20, padding: '8px 16px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 4, color: '#ff4d4f', cursor: 'pointer' }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <input
                      type="text"
                      placeholder="Reference / Cheque No (optional)"
                      value={payment.reference || ""}
                      onChange={(e) => handlePaymentChange(idx, 'reference', e.target.value)}
                      style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ddd' }}
                    />
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addPaymentRow}
                style={{ marginBottom: 20, padding: '8px 16px', background: 'transparent', border: '2px dashed #52c41a', borderRadius: 4, color: '#52c41a', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <Plus size={16} /> {isReturn ? 'Add Another Refund Method' : 'Add Another Payment Method'}
              </button>
            </>
          )}

          {/* Payment Info Message */}
          {paymentStatus === 'partial' && (
            <div style={{
              marginBottom: 20,
              padding: '12px 16px',
              borderRadius: 4,
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              color: '#0050b3',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                {isReturn
                  ? 'Partial refund selected. The remaining amount will reduce the customer Accounts Receivable balance.'
                  : 'Partial payment selected. The remaining balance will be recorded as Accounts Receivable.'}
              </span>
            </div>
          )}

          {paymentStatus === 'notpaid' && (
            <div style={{
              marginBottom: 20,
              padding: '12px 16px',
              borderRadius: 4,
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              color: '#d48806',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10
            }}>
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 14, lineHeight: 1.5 }}>
                {isReturn
                  ? 'No immediate refund entered. The full return amount will reduce the customer Accounts Receivable balance.'
                  : 'No immediate payment entered. The full amount will be added to Accounts Receivable.'}
              </span>
            </div>
          )}

          {/* Totals Summary */}
          <div style={{
            background: '#f5f5f5',
            padding: 20,
            borderRadius: 4,
            marginBottom: 20
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Subtotal:</span>
              <strong>{formatCurrency(totals.subTotal)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Total Discount:</span>
              <strong style={{ color: '#e53935' }}>-{formatCurrency(totals.totalDiscount)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Tax:</span>
              <strong>+{formatCurrency(totals.totalTax)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Shipping:</span>
              <strong>+{formatCurrency(totals.shippingCost)}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span>Other:</span>
              <strong>+{formatCurrency(totals.otherCharges)}</strong>
            </div>

            {externalCustomerPaymentsTotal > 0 && (
              <div style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed #1890ff',
                background: '#e6f7ff',
                padding: '10px',
                borderRadius: 4
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <span style={{ fontWeight: 600 }}>Previous Customer Payments:</span>
                  <strong style={{ color: '#1890ff' }}>{formatCurrency(externalCustomerPaymentsTotal)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600 }}>Total Paid (Including Previous):</span>
                  <strong style={{ color: '#52c41a', fontSize: 16 }}>
                    {formatCurrency(totals.totalAmountPaid)}
                  </strong>
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontWeight: 'bold',
              marginTop: 10,
              paddingTop: 10,
              borderTop: '2px solid #ddd'
            }}>
              <span>Grand Total:</span>
              <strong style={{ color: isReturn ? '#ff4d4f' : '#1890ff', fontSize: 18 }}>
                {formatCurrency(totals.grandTotal)}
              </strong>
            </div>

            <div style={{
              marginTop: 15,
              paddingTop: 15,
              borderTop: '1px dashed #ddd'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Amount Paid Now:</span>
                <strong style={{ color: '#52c41a' }}>{formatCurrency(totals.directAmountPaid)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span>Customer Balance (After This Payment):</span>
                <strong style={{
                  color: totals.netAmount > 0 ? '#ff4d4f' : '#52c41a',
                  fontWeight: 'bold'
                }}>
                  {formatCurrency(totals.netAmount)}
                </strong>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
                padding: '8px 12px',
                background: paymentStatus === 'paid' ? '#f6ffed' :
                  paymentStatus === 'partial' ? '#fff7e6' :
                    paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                border: `1px solid ${paymentStatus === 'paid' ? '#b7eb8f' :
                  paymentStatus === 'partial' ? '#ffd591' :
                    paymentStatus === 'notpaid' ? '#ffa39e' : '#91d5ff'
                  }`,
                borderRadius: 4
              }}>
                <span style={{ fontWeight: 600 }}>Payment Status:</span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  background: paymentStatus === 'paid' ? '#f6ffed' :
                    paymentStatus === 'partial' ? '#fff7e6' :
                      paymentStatus === 'notpaid' ? '#fff1f0' : '#e6f7ff',
                  color: paymentStatus === 'paid' ? '#52c41a' :
                    paymentStatus === 'partial' ? '#fa8c16' :
                      paymentStatus === 'notpaid' ? '#f5222d' : '#1890ff'
                }}>
                  {paymentStatus === 'paid' ? 'Paid' :
                    paymentStatus === 'partial' ? 'Partial' :
                      paymentStatus === 'notpaid' ? 'Not Paid' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 20 }}>
            <textarea
              name="notes"
              placeholder="Additional notes..."
              value={formData.notes}
              onChange={handleChange}
              style={{ width: '100%', padding: 8, minHeight: 80, borderRadius: 4, border: '1px solid #ddd' }}
            />
          </div>

          {/* ✅ Overpayment / Over-refund warning — works in BOTH Sales and Return mode */}
          {hasOverpayment && (
            <div
              style={{
                marginBottom: 15,
                padding: '12px 16px',
                background: '#fff1f0',
                border: '1px solid #ffa39e',
                borderLeft: '4px solid #f5222d',
                borderRadius: 4,
                color: '#cf1322',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <AlertCircle size={18} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ fontSize: 14, lineHeight: 1.5 }}>
                <strong style={{ display: 'block', marginBottom: 4 }}>
                  {isReturn
                    ? "Refund amount cannot exceed the return total."
                    : "Payment amount cannot exceed the sales total."}
                </strong>
                <div style={{ marginBottom: 4 }}>
                  Total: <strong>{formatCurrency(Math.abs(totals.grandTotal))}</strong>
                  {" • "}
                  Entered: <strong>{formatCurrency(totals.totalAmountPaid)}</strong>
                  {externalCustomerPaymentsTotal > 0 && (
                    <>
                      {" "}
                      <span style={{ color: '#8c8c8c' }}>
                        (includes {formatCurrency(externalCustomerPaymentsTotal)} previous customer payments)
                      </span>
                    </>
                  )}
                  {" • "}
                  Excess: <strong style={{ color: '#f5222d' }}>{formatCurrency(overpaidAmount)}</strong>
                </div>
                <div>
                  Reduce the {isReturn ? "refund" : "payment"} by{" "}
                  <strong>{formatCurrency(overpaidAmount)}</strong> to continue.
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={handleCancel}
              disabled={submitting}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid #ddd',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <X size={16} /> Cancel
            </button>
            <button
              type="submit"
              disabled={
                submitting ||
                hasOverpayment ||
                transactionStatus === "cancelled" ||
                transactionStatus === "returned"
              }
              title={hasOverpayment
                ? `${isReturn ? "Refund" : "Payment"} exceeds total by ${formatCurrency(overpaidAmount)}`
                : ""}
              style={{
                padding: '10px 20px',
                background:
                  transactionStatus === 'cancelled' ||
                    transactionStatus === 'returned' ||
                    hasOverpayment
                    ? '#d9d9d9'
                    : '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: 4,
                cursor:
                  transactionStatus === 'cancelled' ||
                    transactionStatus === 'returned' ||
                    hasOverpayment
                    ? 'not-allowed'
                    : submitting ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isFinanciallyPosted ? 'Update Posted Sales' : 'Update Sales'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditSalesTransaction;